<div class="page-container">
  <app-card>
    <app-card-content>
      <div class="diagnostic-section">
        <h3>{{ 'diagnostics.appInfo.title' | translate }}</h3>
        <p><strong>{{ 'diagnostics.appInfo.name' | translate }}</strong> {{ appName }}</p>
        <p data-testid="diagnostics-runtime"><strong>{{ 'diagnostics.appInfo.runningIn' | translate }}</strong>
          @if (isNativeApp) {
          {{ 'diagnostics.appInfo.runningInApp' | translate }} ({{ nativePlatformName }})
          } @else {
          {{ 'diagnostics.appInfo.runningInBrowser' | translate }}
          }
        </p>
        @if (isNativeApp) {
        <p data-testid="diagnostics-native-build"><strong>{{ 'diagnostics.appInfo.nativeBuild' | translate }}</strong>
          {{ nativeBuild() ?? '—' }}</p>
        <p data-testid="diagnostics-bundle"><strong>{{ 'diagnostics.appInfo.webBundle' | translate }}</strong>
          {{ liveUpdateBundleId() ?? ('diagnostics.appInfo.webBundleBuiltIn' | translate) }}</p>
        }
        <p><strong>{{ 'diagnostics.appInfo.version' | translate }}</strong> {{ buildInfo.version }}</p>
        <p><strong>{{ 'diagnostics.appInfo.buildTime' | translate }}</strong> {{ buildInfo.timestamp | dateTimezone }}
        </p>
        <p><strong>{{ 'diagnostics.appInfo.gitHash' | translate }}</strong> {{ buildInfo.gitHash }}</p>
        <p><strong>{{ 'diagnostics.appInfo.environment' | translate }}</strong> {{ environment.name }}</p>
        <p><strong>{{ 'diagnostics.appInfo.apiUrl' | translate }}</strong> {{ environment.apiUrl }}</p>
        <p><strong>{{ 'diagnostics.appInfo.production' | translate }}</strong> {{ environment.production ? 'Yes' : 'No'
          }}</p>
        <p><strong>{{ 'diagnostics.appInfo.testAuthEnabled' | translate }}</strong> {{ environment.enableTestAuth ?
          'Yes' : 'No' }}</p>

        <div class="action-row">
          <app-button variant="outlined" (click)="triggerInstallPrompt()">
            {{ 'diagnostics.appInfo.showInstallPrompt' | translate }}
          </app-button>
        </div>

        <details>
          <summary>{{ 'diagnostics.appInfo.advancedInfo' | translate }}</summary>
          <div class="advanced-info">
            <p><strong>{{ 'diagnostics.appInfo.artworkPath' | translate }}</strong> {{ environment.artworkPath }}</p>
            <p><strong>{{ 'diagnostics.appInfo.showClickableQrUrls' | translate }}</strong> {{
              environment.showClickableQrUrls ? 'Yes' : 'No' }}</p>
            <p><strong>{{ 'diagnostics.appInfo.oauthClientId' | translate }}</strong> {{
              environment.oauth.google.clientId
              }}</p>
            <p><strong>{{ 'diagnostics.appInfo.oauthRedirectUri' | translate }}</strong> {{
              environment.oauth.google.redirectUri }}</p>
          </div>
        </details>
      </div>

      <div class="diagnostic-section">
        <h3>{{ 'diagnostics.backendInfo.title' | translate }}</h3>
        <p>{{ 'diagnostics.backendInfo.description' | translate }}</p>

        @if (networkState.stayOfflineMode()) {
        <div class="offline-placeholder">
          <app-chip color="warning">
            <app-icon name="wifi-off" />
            {{ 'diagnostics.backendInfo.offlineMode' | translate }}
          </app-chip>
          <p class="text-muted">{{ 'diagnostics.backendInfo.offlineModeDescription' | translate }}</p>
        </div>
        } @else if (backendEnvironmentLoading()) {
        <div class="loading-state">
          <app-chip color="primary">
            <app-icon name="loader" />
            {{ 'diagnostics.backendInfo.loading' | translate }}
          </app-chip>
        </div>
        } @else if (backendEnvironmentError()) {
        <div class="error-state">
          <app-chip color="danger">
            <app-icon name="alert-circle" />
            {{ backendEnvironmentError() }}
          </app-chip>
          <app-button variant="outlined" (click)="refreshBackendEnvironmentInfo()" data-testid="retry-backend-info">
            <app-icon name="refresh-cw" />
            {{ 'diagnostics.backendInfo.retry' | translate }}
          </app-button>
        </div>
        } @else if (backendEnvironmentInfo()) {
        <div class="backend-info-grid">
          <p><strong>{{ 'diagnostics.backendInfo.environment' | translate }}</strong> {{
            backendEnvironmentInfo()!.environment }}</p>
          <p><strong>{{ 'diagnostics.backendInfo.version' | translate }}</strong> {{ backendEnvironmentInfo()!.version
            }}</p>
          <p><strong>{{ 'diagnostics.backendInfo.buildTime' | translate }}</strong> {{
            backendEnvironmentInfo()!.buildTimestamp | dateTimezone }}</p>
          <p><strong>{{ 'diagnostics.backendInfo.gitHash' | translate }}</strong> {{ backendEnvironmentInfo()!.gitHash
            }}</p>
          <p><strong>{{ 'diagnostics.backendInfo.isProduction' | translate }}</strong> {{
            backendEnvironmentInfo()!.config.isProduction ? 'Yes' : 'No' }}</p>
          <p><strong>{{ 'diagnostics.backendInfo.testAuthEnabled' | translate }}</strong> {{
            backendEnvironmentInfo()!.config.enableTestAuth ? 'Yes' : 'No' }}</p>

          <details>
            <summary>{{ 'diagnostics.backendInfo.advancedInfo' | translate }}</summary>
            <div class="advanced-info">
              <p><strong>{{ 'diagnostics.backendInfo.serverPort' | translate }}</strong> {{
                backendEnvironmentInfo()!.server.port }}</p>
              <p><strong>{{ 'diagnostics.backendInfo.frontendUrl' | translate }}</strong> {{
                backendEnvironmentInfo()!.config.frontendUrl }}</p>
              <p><strong>{{ 'diagnostics.backendInfo.appRoot' | translate }}</strong> {{
                backendEnvironmentInfo()!.database.appRoot }}</p>
              <p><strong>{{ 'diagnostics.backendInfo.hasGlobalDb' | translate }}</strong> {{
                backendEnvironmentInfo()!.database.hasGlobalDatabase ? 'Yes' : 'No' }}</p>
            </div>
          </details>
        </div>
        }
      </div>

      <div class="diagnostic-section">
        <h3>{{ 'diagnostics.apiConnection.title' | translate }}</h3>
        <div class="flex gap-md flex-align-center">
          <span>{{ 'diagnostics.apiConnection.description' | translate }}</span>
          <app-button variant="icon" (click)="testApi()" data-testid="test-api-button"
            [tippy]="'diagnostics.apiConnection.test' | translate">
            <app-icon name="refresh-cw" />
          </app-button>
        </div>

        @if (apiStatus()) {
        <div class="status-chip">
          <app-chip [color]="apiStatus() === 'connected' ? 'success' : 'danger'">
            <app-icon [name]="apiStatus() === 'connected' ? 'check-circle' : 'alert-circle'" />
            {{ 'diagnostics.apiConnection.status' | translate:{ status: apiStatus() || '' } }}
          </app-chip>
        </div>
        }
      </div>

      <div class="diagnostic-section">
        <h3>{{ 'diagnostics.sync.title' | translate }}</h3>
        <p>{{ 'diagnostics.sync.description' | translate }}</p>
        <div class="status-chip">
          <app-chip [color]="syncService.connected() ? 'success' : 'danger'">
            <app-icon [name]="syncService.connected() ? 'check-circle' : 'alert-circle'" />
            {{ syncService.connected() ? ('diagnostics.sync.connected' | translate) : ('diagnostics.sync.disconnected' |
            translate) }}
          </app-chip>
        </div>
      </div>

      <div class="diagnostic-section">
        <h3>{{ 'diagnostics.notifications.title' | translate }}</h3>
        <p>{{ 'diagnostics.notifications.description' | translate }}</p>
        <div class="checkbox-container">
          <app-checkbox [(ngModel)]="autoDismiss" [label]="'diagnostics.notifications.autoDismiss' | translate"
            data-testid="auto-dismiss-checkbox" />
        </div>
        <div class="button-group">
          <app-button variant="filled" class="toast-error" (click)="testErrorToast()" data-testid="test-error-toast">
            {{ 'diagnostics.notifications.error' | translate }}
          </app-button>
          <app-button variant="filled" class="toast-warning" (click)="testWarningToast()"
            data-testid="test-warning-toast">
            {{ 'diagnostics.notifications.warning' | translate }}
          </app-button>
          <app-button variant="filled" class="toast-info" (click)="testInfoToast()" data-testid="test-info-toast">
            {{ 'diagnostics.notifications.info' | translate }}
          </app-button>
          <app-button variant="filled" class="toast-success" (click)="testSuccessToast()"
            data-testid="test-success-toast">
            {{ 'diagnostics.notifications.success' | translate }}
          </app-button>
        </div>
      </div>

      <div class="diagnostic-section">
        <h3>{{ 'diagnostics.logging.title' | translate }}</h3>
        <p>{{ 'diagnostics.logging.description' | translate }}</p>
        <app-button variant="outlined" (click)="viewLogging()" data-testid="view-logging-button">
          <app-icon name="terminal" />
          {{ 'diagnostics.logging.button' | translate }}
        </app-button>
      </div>

      <div class="diagnostic-section">
        <h3>Mobile Debugger</h3>
        <p>Eruda provides on-device developer tools for mobile debugging, including console, network inspection, and
          element viewer. Use the floating button to toggle the debugger panel.</p>
        <div class="checkbox-group">
          <app-checkbox [ngModel]="erudaService.isEnabled()" (ngModelChange)="toggleErudaEnabled($event)"
            label="Enable Eruda Debugger" data-testid="enable-eruda-checkbox" />
        </div>
        <div class="checkbox-group">
          <app-checkbox [ngModel]="streamLogsEnabled()" (ngModelChange)="toggleStreamLogs($event)"
            label="Stream logs to backend (auto-off in 1 hour)" data-testid="stream-logs-checkbox" />
        </div>
        @if (streamLogsEnabled() && streamLogsExpiresAt()) {
        <p class="text-muted" data-testid="stream-logs-expiry">
          Streaming active — auto-off at {{ streamLogsExpiresAt()! | date:'h:mm a' }}
        </p>
        }
        <div class="button-group">
          <app-button variant="outlined" (click)="copyErudaLogs()" data-testid="copy-eruda-logs-button">
            <app-icon name="copy" />
            Copy All Logs
          </app-button>
          <app-button variant="filled" class="btn-danger" (click)="nuclearReset()" [disabled]="isNuclearResetting()"
            [tippy]="'db-test.nuclear-reset-warning' | translate" data-testid="nuclear-reset-button">
            💥 {{ isNuclearResetting() ? 'Resetting...' : 'Nuclear Reset' }}
          </app-button>
        </div>
      </div>

      <div class="diagnostic-section">
        <h3>{{ 'diagnostics.networkActivity.title' | translate }}</h3>
        <p>{{ 'diagnostics.networkActivity.description' | translate }}</p>
        <app-button variant="outlined" (click)="viewNetworkActivity()" data-testid="view-network-activity-button">
          <app-icon name="list" />
          {{ 'diagnostics.networkActivity.button' | translate }}
        </app-button>
      </div>

    </app-card-content>
  </app-card>
</div>